import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';
import { CONTACT } from '../lib/data';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'About Us', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Contact', to: '/contact' },
];

const Logo = ({ light }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <span className={`flex h-9 w-9 items-center justify-center font-display text-lg font-semibold transition-colors ${light ? 'bg-white text-primary' : 'bg-primary text-primary-foreground'}`}>
      SP
    </span>
    <span className={`font-display text-lg font-semibold tracking-tight leading-none transition-colors ${light ? 'text-white' : 'text-foreground'}`}>
      Shiva Prints<span className="text-accent"> &amp; Packs</span>
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const light = !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || open ? 'bg-white/90 backdrop-blur-xl border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Logo light={light} />

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline ${isActive ? 'text-accent' : light ? 'text-white/85 hover:text-white' : 'text-foreground/80 hover:text-foreground'}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.tel}
            data-testid="nav-call-now"
            className={`hidden sm:inline-flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors duration-300 ${light ? 'bg-white text-primary hover:bg-accent hover:text-white' : 'bg-primary text-primary-foreground hover:bg-accent'}`}
          >
            <Phone className="h-4 w-4" />
            Call Now
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className={`md:hidden p-2 transition-colors ${light ? 'text-white' : 'text-foreground'}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-white border-b border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `text-base font-medium ${isActive ? 'text-accent' : 'text-foreground'}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a
                href={CONTACT.tel}
                data-testid="nav-mobile-call"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-sm font-medium"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
